import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import DriveImages from "assets/driveImages"
import { IDLE, PENDING, ASC } from "const"
import { MtDriveFolder, MtDriveFile } from "server/models"
import { StateType } from "client/utils"
import { sort } from "client/store/utils"
import {
    StoreType,
    NestedFolder,

    LoadNestedFolders,
    LoadDriveFolderByProjectId,
    MoveDriveFolder,
    MoveDriveFile,

    setDriveAction,
    clearDriveSelection,
    reloadDriveLists,
    createFolderName,
} from "client/store"
import {
    Button,
    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
    SpinnerIcon,
    Loader,
} from "client/components"
import {
    FolderListContent,
    FolderListWrapper,
    FolderWrapper,
    Folder,
    FolderImage,
    FolderTitle,
    FolderCollapseControl,
} from "./styled"

const COLLAPSED = "collapsed"

export const Move = () => {
    const folderActive = useSelector((state: StoreType) => state.drive.folder.active)
    const foldersSelected = useSelector((state: StoreType) => state.drive.folder.selected)
    const fileActive = useSelector((state: StoreType) => state.drive.file.active)
    const filesSelected = useSelector((state: StoreType) => state.drive.file.selected)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [nestedFolder, setNestedFolder] = useState<NestedFolder>(null)
    const [targetFolder, setTargetFolder] = useState<MtDriveFolder>(null)
    const [loadState, setLoadState] = useState<StateType>(PENDING)
    const [moveState, setMoveState] = useState<StateType>(IDLE)

    const folders = folderActive ? [folderActive] : foldersSelected
    const files = fileActive ? [fileActive] : filesSelected

    const moveFolderIds: number[] = []
    folders.forEach((folder) => {
        moveFolderIds.push(folder.id)
    })

    useEffect(() => {
        // load all folders in the root and only project folders when project is opened
        const promise = !project.id
            ? LoadNestedFolders(userData.accountId, null, lang)
            : LoadDriveFolderByProjectId(project.id).then((projectFolder) => (
                LoadNestedFolders(userData.accountId, projectFolder, lang)
            ))

        promise.then((nested) => {
            setNestedFolder(nested)
            setLoadState(IDLE)
        })
    }, [])

    const onCancel = () => {
        dispatch(setDriveAction(null))
    }

    const onSelect = (folder: MtDriveFolder) => {
        if (targetFolder === folder) setTargetFolder(null)
        else setTargetFolder(folder)
    }

    const onConfirm = () => {
        setMoveState(PENDING)
        const promiseArray: Promise<any>[] = []

        folders.forEach((folder) => {
            const moveFolder: MtDriveFolder = {
                ...folder,
                folderId: targetFolder.id,
            }
            promiseArray.push(MoveDriveFolder(moveFolder))
        })

        files.forEach((file) => {
            const moveFile: MtDriveFile = {
                ...file,
                folderId: targetFolder.id,
            }
            promiseArray.push(MoveDriveFile(moveFile))
        })

        Promise.all(promiseArray).then(() => {
            setMoveState(IDLE)

            clearDriveSelection(dispatch)
            reloadDriveLists(dispatch)
        })
    }

    return (
        <ConfirmWrapper show onClose={onCancel}>
            <Confirm onClick={(e) => e.stopPropagation()}>
                <ConfirmTitle>{lang.confirmMoveTitle}</ConfirmTitle>
                <ConfirmDescription>
                    <FolderListContent>
                        {
                            loadState === PENDING
                            && <Loader paddingTop={80} />
                        }
                        {
                            nestedFolder && (
                                <FolderWrapper>
                                    <Folder
                                        selected={targetFolder && targetFolder.id === nestedFolder.folder.id}
                                        inactive={false}
                                        onClick={() => onSelect(nestedFolder.folder)}
                                    >
                                        <FolderImage src={DriveImages.folder} />
                                        <FolderTitle>{createFolderName(nestedFolder.folder)}</FolderTitle>
                                    </Folder>

                                    <FolderList
                                        nested={Object.values(nestedFolder.folders)}
                                        selected={targetFolder}
                                        inactive={moveFolderIds}
                                        onSelect={onSelect}
                                    />
                                </FolderWrapper>
                            )
                        }
                    </FolderListContent>
                </ConfirmDescription>
                <ConfirmBottom>
                    <Button
                        variant="success"
                        row
                        disabled={!targetFolder}
                        onClick={onConfirm}
                    >
                        {moveState === PENDING ? <SpinnerIcon /> : lang.move}
                    </Button>
                    <Button row onClick={onCancel}>
                        {lang.cancel}
                    </Button>
                </ConfirmBottom>
            </Confirm>
        </ConfirmWrapper>
    )
}
export default Move


interface FolderListProps {
    nested: NestedFolder[]
    selected: MtDriveFolder
    inactive: number[]
    onSelect: (target: MtDriveFolder) => void
}
export const FolderList = ({
    nested,
    selected,
    inactive,
    onSelect,
}: FolderListProps) => {
    const openFolder = (e: React.MouseEvent) => {
        e.stopPropagation()
        const folderWrapper = e.currentTarget.closest(FolderWrapper)
        const collapsed = folderWrapper.classList.contains(COLLAPSED)

        if (collapsed) folderWrapper.classList.remove(COLLAPSED)
        else folderWrapper.classList.add(COLLAPSED)
    }

    return (
        <FolderListWrapper>
            {
                nested.sort((a, b) => {
                    const A: MtDriveFolder = { ...a.folder, name: createFolderName(a.folder) }
                    const B: MtDriveFolder = { ...b.folder, name: createFolderName(b.folder) }
                    return sort(A, B, "name", ASC)
                }).map((nestedFolder) => {
                    const { folder, folders } = nestedFolder
                    const nestedFolders = Object.values(folders)

                    if (!folder.id) return null // don't show Recycle in the list
                    return (
                        <FolderWrapper key={folder.id} className={COLLAPSED}>
                            <Folder
                                selected={selected && selected.id === folder.id}
                                inactive={inactive.includes(folder.id)}
                                onClick={() => onSelect(folder)}
                                onDoubleClick={openFolder}
                            >
                                {
                                    nestedFolders.length > 0
                                    && <FolderCollapseControl onClick={openFolder} />
                                }
                                <FolderImage src={DriveImages.folder} />
                                <FolderTitle>{createFolderName(folder)}</FolderTitle>
                            </Folder>

                            <FolderList
                                nested={nestedFolders}
                                selected={selected}
                                inactive={inactive}
                                onSelect={onSelect}
                            />
                        </FolderWrapper>
                    )
                })
            }
        </FolderListWrapper>
    )
}
